import { Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy, inject } from '@angular/core';
import { CommunicationLogUiText } from '../../communicationlog-uitexts';
import { CommunicationLogEntry } from '../../model/communication-log-entry.model';
import { GenericValue } from '../../../core/model/generic-value';
import { HisUiService } from '../../../core/service/base/his-ui.service';
import { isEmpty } from 'lodash';
import { hasDisplayProperties } from '../../service/communicationlog-entry-util';
import { HisNavigationService } from '../../../core/service/base/his-navigation.service';

export type DisplayData = {
    id: number,
    title: string,
    hasContent: boolean,
    contentString?: string,
    displayProperties?: { key: string, value: string }[],
    linkUrl?: string,
    createdAt: Date,
    senderName: string,
    isIncoming: boolean,
    editable: boolean,
    deletable: boolean,
    hasDownload: boolean,
    domains: Domain[]
}

type Domain = {
    id: number,
    uniquename: string,
    longtext: string
}

@Component({
    selector: 'his-communication-log-entry',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './communication-log-entry.component.html',
    styleUrls: ['./communication-log-entry.component.css'],
    standalone: false
})
export class CommunicationLogEntryComponent implements OnInit {
    private uiService = inject(HisUiService);
    private navivationService = inject(HisNavigationService);

    @Input( { required: true } ) entry: CommunicationLogEntry;
    @Input( { required: true } ) domainsByIdMap: Map<number, GenericValue>;

    @Output() deleteEntry: EventEmitter<number> = new EventEmitter<number>();
    @Output() editEntry: EventEmitter<number> = new EventEmitter<number>();

    private baseUrl: string;

    protected get displayData(): DisplayData {
        return this.transformToDisplayData(this.entry);
    }

    protected uiText = CommunicationLogUiText;

    ngOnInit(): void {
        this.baseUrl = this.navivationService.getQisserverUrl();
    }

    private transformToDisplayData(entry: CommunicationLogEntry): DisplayData {
        return {
            id: entry.id,
            title: this.getHeaderString( entry ),
            hasContent: this.hasContent( entry ),
            createdAt: entry.createdAt,
            senderName: entry.senderName,
            isIncoming: entry.incomming,
            editable: entry.editable,
            deletable: entry.deletable,
            hasDownload: entry.isDocumentEntry,
            linkUrl: this.getLinkUrl( entry ),
            contentString: this.getContentString( entry ),
            displayProperties: this.getDisplayProperties( entry ),
            domains: entry.domainIds?.map( id => this.domainsByIdMap?.get( id ) ).filter( Boolean )
                .map( domain => ( {
                    id: domain.id,
                    uniquename: domain.uniquename,
                    longtext: domain.longtext
                } ) )
        };
    }
    private getContentString( entry: CommunicationLogEntry ): string | undefined {
        return entry.additionalAttributes?.text || undefined;
    }

    private getDisplayProperties( entry: CommunicationLogEntry ): { key: string, value: string }[] | undefined {
        if ( !hasDisplayProperties( entry ) ) {
            return undefined;
        }

        const displayProperties = entry.additionalAttributes?.displayProperties;
        if ( !displayProperties ) {
            return undefined;
        }

        return Object.entries( displayProperties )
            .map( ( [key, value] ) => ( {
                key: this.uiService.translate( key ),
                value: value as string
            } ) );
    }

    private getLinkUrl( entry: CommunicationLogEntry ): string | undefined {
        if ( entry.isDocumentEntry ) {
            return this.getDocumentDownloadUrl( entry );
        }

        const linkUrl = entry.linkUrl;
        if ( isEmpty( linkUrl ) ) {
            return undefined;
        }

        return `${this.baseUrl}/${linkUrl}`;
    }

    private getDocumentDownloadUrl( entry: CommunicationLogEntry ): string | undefined {
        if ( isEmpty( entry.referenceId ) ) {
            return undefined;
        }

        return `${this.baseUrl}/rds?state=docdownload&docId=${entry.referenceId}`;
    }
    private getHeaderString( entry: CommunicationLogEntry ): string {
        return this.uiService.translate( `${this.uiText.COMMUNICATION_LOG_ENTRY_TITLE}.${entry.type}`, ...entry.titleMessageParams );
    }

    private hasContent( entry: CommunicationLogEntry ): boolean {
        if ( entry.isDocumentEntry ) {
            return true;
        }

        return !isEmpty( this.getLinkUrl( entry ) ) || !isEmpty( this.getContentString( entry ) );
    }
}
